// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const errorId = error ? 'reset-password-error' : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token.trim() || !password.trim()) {
      setError('Token and new password are required.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await api.post('/auth/member/reset-password', {
        token: token.trim(),
        new_password: password.trim()
      });
      navigate('/login');
    } catch (err: unknown) {
      let errorMessage = 'Unable to reset password. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string }; status?: number } };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-brand-50 via-white to-white px-4 py-10">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-md">
        <h1 className="mb-2 text-center text-2xl font-semibold text-brand-700">Choose a new password</h1>
        <p className="mb-6 text-center text-sm text-slate-600">
          Enter the reset token from your email and choose a new password.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} aria-describedby={errorId} noValidate>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="token">
              Reset token
            </label>
            <input
              id="token"
              type="text"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="Paste your reset token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="Enter a strong passphrase"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          {error ? (
            <p id={errorId} role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : (
            <p className="text-xs text-slate-500">Choose a strong passphrase you can remember.</p>
          )}
          <button
            type="submit"
            className="w-full rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-200"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Reset password'}
          </button>
          <div className="text-center text-sm">
            <Link to="/login" className="text-brand-600 hover:text-brand-700">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
